/**
 * FikoRemoteCamera - WebRTC Mobil Kamera Bağlantısı
 *
 * Cep telefonu kamerasını Socket.IO/WebRTC üzerinden PC'ye bağlar.
 * QR kod ile eşleştirme, otomatik yeniden bağlanma destekler.
 */

import { BehaviorSubject, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';

// @ts-ignore - qrcode modülü tip tanımlaması yok
import QRCode from 'qrcode/lib/browser';

/** Bağlantı durumları */
export type ConnectionState = 'disconnected' | 'connecting' | 'pairing' | 'connected' | 'error';

/** Kamera bilgisi */
export interface RemoteCameraInfo {
    deviceId: string;
    deviceName: string;
    resolution: { width: number; height: number };
    fps: number;
    batteryLevel?: number;
}

/** Eşleştirme bilgisi */
export interface PairingInfo {
    sessionId: string;
    pairingCode: string;
    qrDataUrl: string;
    mobileUrl: string;
    expiresAt: Date;
}

/** IMU Sensör Verileri (telefondan gelen) */
export interface IMUSensorData {
    /** DeviceOrientation - alpha (Z ekseni, 0-360) */
    alpha: number;
    /** DeviceOrientation - beta (X ekseni, -180 to 180) */
    beta: number;
    /** DeviceOrientation - gamma (Y ekseni, -90 to 90) */
    gamma: number;
    /** DeviceMotion - X ivme (m/s²) */
    accelerationX: number;
    /** DeviceMotion - Y ivme (m/s²) */
    accelerationY: number;
    /** DeviceMotion - Z ivme (m/s²) */
    accelerationZ: number;
    /** DeviceMotion - alpha rotation rate (°/s) */
    rotationRateAlpha: number;
    /** DeviceMotion - beta rotation rate (°/s) */
    rotationRateBeta: number;
    /** DeviceMotion - gamma rotation rate (°/s) */
    rotationRateGamma: number;
    /** Zaman damgası (ms) */
    timestamp: number;
}

/** WebRTC ICE sunucu yapılandırması */
const ICE_SERVERS: RTCIceServer[] = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
];

/**
 * Socket.IO + WebRTC tabanlı uzak kamera bağlantı servisi
 */
export class FikoRemoteCamera {
    /** Bağlantı durumu */
    public readonly state$ = new BehaviorSubject<ConnectionState>('disconnected');

    /** Kamera bilgisi */
    public readonly cameraInfo$ = new BehaviorSubject<RemoteCameraInfo | null>(null);

    /** Video stream */
    public readonly videoStream$ = new BehaviorSubject<MediaStream | null>(null);

    /** Frame yakalandığında */
    public readonly frameCapture$ = new Subject<ImageData>();

    /** Hata mesajları */
    public readonly error$ = new Subject<string>();

    /** Eşleştirme bilgisi */
    public readonly pairing$ = new BehaviorSubject<PairingInfo | null>(null);

    /** Tarama kontrol komutları (telefon'dan) */
    public readonly scanControl$ = new Subject<{ command: 'start' | 'stop' | 'complete'; name?: string }>();

    /** IMU sensör verileri (telefon'dan) */
    public readonly imuData$ = new Subject<IMUSensorData>();

    /** Son IMU verisi (sensor fusion için) */
    private lastIMUData: IMUSensorData | null = null;

    // Socket.IO
    private socket: Socket | null = null;

    // WebRTC bileşenleri
    private peerConnection: RTCPeerConnection | null = null;
    private dataChannel: RTCDataChannel | null = null;

    // Session bilgisi
    private sessionId: string = '';
    private pairingCode: string = '';

    // Sunucu URL'leri
    private serverUrl: string;
    private mobilePageUrl: string;

    // Frame yakalama
    private videoElement: HTMLVideoElement | null = null;
    private captureCanvas: HTMLCanvasElement | null = null;
    private captureContext: CanvasRenderingContext2D | null = null;
    private captureInterval: any = null;

    constructor(serverUrl?: string) {
        // Sunucu URL'ini belirle (Socket.IO için Go sunucusu)
        this.serverUrl = serverUrl || this.detectServerUrl();

        // Mobil sayfa URL'i (Angular route - aynı origin)
        this.mobilePageUrl = window.location.origin + '/cmm';

        this.setupCaptureCanvas();
    }

    /**
     * Sunucu URL'ini otomatik algıla
     * Socket.IO bağlantısı için Go sunucu adresi (port 9000)
     */
    private detectServerUrl(): string {
        const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
        const host = window.location.hostname;

        // Go sunucu portu her zaman 9000
        const GO_SERVER_PORT = '9000';

        return `${protocol}//${host}:${GO_SERVER_PORT}`;
    }

    /**
     * Yakalama canvas'ını hazırla
     */
    private setupCaptureCanvas(): void {
        this.captureCanvas = document.createElement('canvas');
        // willReadFrequently: getImageData çok kullanılacak, performans için
        this.captureContext = this.captureCanvas.getContext('2d', { willReadFrequently: true });
    }

    /**
     * Eşleştirme başlat - QR kod oluştur
     */
    async startPairing(): Promise<PairingInfo> {
        this.state$.next('pairing');

        try {
            // Benzersiz session ID ve pairing code oluştur
            this.sessionId = this.generateSessionId();
            this.pairingCode = this.generatePairingCode();

            // Socket.IO bağlantısı kur
            await this.connectSocket();

            // Session'ı başlat
            this.socket?.emit('FikoYT.cmm.pairing.start', {
                sessionId: this.sessionId,
                pairingCode: this.pairingCode
            });

            // Mobil sayfa URL'i oluştur
            const mobileUrl = `${this.mobilePageUrl}?session=${this.sessionId}&code=${this.pairingCode}`;

            // QR kod oluştur (mobil sayfaya yönlendiren)
            const qrDataUrl = await this.generateQRCode(mobileUrl);

            const pairingInfo: PairingInfo = {
                sessionId: this.sessionId,
                pairingCode: this.pairingCode,
                qrDataUrl: qrDataUrl,
                mobileUrl: mobileUrl,
                expiresAt: new Date(Date.now() + 5 * 60 * 1000) // 5 dakika
            };

            this.pairing$.next(pairingInfo);

            console.log('[RemoteCamera] Eşleştirme başlatıldı:', this.sessionId);
            console.log('[RemoteCamera] Mobil URL:', mobileUrl);

            return pairingInfo;

        } catch (error: any) {
            this.state$.next('error');
            this.error$.next(`Eşleştirme başlatılamadı: ${error.message}`);
            throw error;
        }
    }

    /**
     * Socket.IO bağlantısı kur
     */
    private async connectSocket(): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                console.log('[RemoteCamera] Socket.IO bağlanıyor:', this.serverUrl);

                this.socket = io(this.serverUrl, {
                    transports: ['websocket', 'polling'],
                    reconnection: true,
                    reconnectionAttempts: 5,
                    reconnectionDelay: 1000,
                    timeout: 10000
                });

                this.socket.on('connect', () => {
                    console.log('[RemoteCamera] Socket.IO bağlandı:', this.socket?.id);
                    resolve();
                });

                this.socket.on('connect_error', (error) => {
                    console.error('[RemoteCamera] Socket.IO bağlantı hatası:', this.serverUrl, error);
                    reject(new Error(`Go sunucusuna bağlanılamadı (${this.serverUrl}). Sunucu çalışıyor mu?`));
                });

                // Mobil bağlandı
                this.socket.on('FikoYT.cmm.mobile.connected', (data) => {
                    console.log('[RemoteCamera] Mobil bağlandı:', data);
                    this.state$.next('connecting');

                    // Kamera bilgisi gelirse kaydet
                    if (data.deviceName) {
                        this.cameraInfo$.next({
                            deviceId: data.mobileId || '',
                            deviceName: data.deviceName,
                            resolution: { width: 0, height: 0 },
                            fps: 30
                        });
                    }

                    // WebRTC başlat
                    this.initWebRTC();
                });

                // Kamera bilgisi
                this.socket.on('FikoYT.cmm.camera.info', (data) => {
                    console.log('[RemoteCamera] Kamera bilgisi:', data);
                    this.cameraInfo$.next(data.info);
                });

                // WebRTC Offer (mobil'den)
                this.socket.on('FikoYT.cmm.webrtc.offer', async (data) => {
                    console.log('[RemoteCamera] WebRTC Offer alındı');
                    if (data.from === 'mobile') {
                        await this.handleOffer(data.offer);
                    }
                });

                // WebRTC Answer (mobil'den)
                this.socket.on('FikoYT.cmm.webrtc.answer', async (data) => {
                    console.log('[RemoteCamera] WebRTC Answer alındı');
                    if (data.from === 'mobile') {
                        await this.handleAnswer(data.answer);
                    }
                });

                // WebRTC ICE (mobil'den)
                this.socket.on('FikoYT.cmm.webrtc.ice', async (data) => {
                    if (data.from === 'mobile' && data.candidate) {
                        await this.handleIceCandidate(data.candidate);
                    }
                });

                // Durum değişti
                this.socket.on('FikoYT.cmm.state.changed', (data) => {
                    console.log('[RemoteCamera] Durum değişti:', data);
                    if (data.state === 'connected') {
                        this.state$.next('connected');
                        this.pairing$.next(null);
                    }
                });

                // Bağlantı kesildi
                this.socket.on('FikoYT.cmm.disconnected', (data) => {
                    console.log('[RemoteCamera] Bağlantı kesildi:', data);
                    this.handleDisconnect();
                });

                this.socket.on('disconnect', () => {
                    console.log('[RemoteCamera] Socket.IO bağlantısı kesildi');
                    if (this.state$.value !== 'disconnected') {
                        this.state$.next('error');
                    }
                });

                // Tarama kontrol komutları (telefon'dan gelen)
                this.socket.on('FikoYT.cmm.scan.control', (data) => {
                    console.log('[RemoteCamera] Tarama kontrol komutu alındı:', data);
                    if (data.sessionId === this.sessionId) {
                        this.scanControl$.next({
                            command: data.command,
                            name: data.name
                        });
                    }
                });

                // IMU sensör verileri (telefon'dan gelen - 30 FPS)
                this.socket.on('FikoYT.cmm.imu.data', (data) => {
                    if (data.sessionId === this.sessionId && data.imu) {
                        const imuData: IMUSensorData = {
                            alpha: data.imu.alpha || 0,
                            beta: data.imu.beta || 0,
                            gamma: data.imu.gamma || 0,
                            accelerationX: data.imu.accelerationX || 0,
                            accelerationY: data.imu.accelerationY || 0,
                            accelerationZ: data.imu.accelerationZ || 0,
                            rotationRateAlpha: data.imu.rotationRateAlpha || 0,
                            rotationRateBeta: data.imu.rotationRateBeta || 0,
                            rotationRateGamma: data.imu.rotationRateGamma || 0,
                            timestamp: data.imu.timestamp || Date.now()
                        };
                        this.lastIMUData = imuData;
                        this.imuData$.next(imuData);
                    }
                });

            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * WebRTC bağlantısını başlat
     */
    private async initWebRTC(): Promise<void> {
        try {
            // Peer connection oluştur
            this.peerConnection = new RTCPeerConnection({ iceServers: ICE_SERVERS });

            // ICE candidate event
            this.peerConnection.onicecandidate = (event) => {
                if (event.candidate) {
                    this.socket?.emit('FikoYT.cmm.webrtc.ice', {
                        sessionId: this.sessionId,
                        candidate: event.candidate,
                        from: 'pc'
                    });
                }
            };

            // Connection state change
            this.peerConnection.onconnectionstatechange = () => {
                console.log('[RemoteCamera] WebRTC state:', this.peerConnection?.connectionState);

                if (this.peerConnection?.connectionState === 'connected') {
                    this.state$.next('connected');
                    this.pairing$.next(null);

                    this.socket?.emit('FikoYT.cmm.webrtc.connected', {
                        sessionId: this.sessionId
                    });
                } else if (this.peerConnection?.connectionState === 'failed') {
                    this.state$.next('error');
                    this.error$.next('WebRTC bağlantısı başarısız');
                }
            };

            // Track event - video stream alındığında
            this.peerConnection.ontrack = (event) => {
                console.log('[RemoteCamera] Track alındı:', event.track.kind, {
                    enabled: event.track.enabled,
                    muted: event.track.muted,
                    readyState: event.track.readyState,
                    streamCount: event.streams.length
                });

                if (event.track.kind === 'video') {
                    const stream = event.streams[0];
                    if (stream) {
                        console.log('[RemoteCamera] Video stream yayınlanıyor, tracks:', stream.getTracks().length);
                        this.videoStream$.next(stream);
                        this.startFrameCapture(stream);
                    } else {
                        console.error('[RemoteCamera] Stream alınamadı!');
                    }
                }
            };

            // Data channel oluştur
            this.dataChannel = this.peerConnection.createDataChannel('control');
            this.setupDataChannel();

            console.log('[RemoteCamera] WebRTC hazır, mobil offer bekleniyor...');

        } catch (error: any) {
            this.state$.next('error');
            this.error$.next(`WebRTC başlatılamadı: ${error.message}`);
        }
    }

    /**
     * Data channel'ı ayarla
     */
    private setupDataChannel(): void {
        if (!this.dataChannel) return;

        this.dataChannel.onopen = () => {
            console.log('[RemoteCamera] Data channel açıldı');
        };

        this.dataChannel.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                this.handleControlMessage(data);
            } catch (e) {
                console.error('[RemoteCamera] Control message parse hatası:', e);
            }
        };
    }

    /**
     * Kontrol mesajlarını işle
     */
    private handleControlMessage(data: any): void {
        console.log('[RemoteCamera] Control message:', data);
    }

    /**
     * Offer'ı işle (mobil'den)
     */
    private async handleOffer(offer: RTCSessionDescriptionInit): Promise<void> {
        if (!this.peerConnection) {
            await this.initWebRTC();
        }

        if (!this.peerConnection) return;

        try {
            await this.peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
            const answer = await this.peerConnection.createAnswer();
            await this.peerConnection.setLocalDescription(answer);

            this.socket?.emit('FikoYT.cmm.webrtc.answer', {
                sessionId: this.sessionId,
                answer: answer,
                from: 'pc'
            });

            console.log('[RemoteCamera] Answer gönderildi');
        } catch (error) {
            console.error('[RemoteCamera] Offer işleme hatası:', error);
        }
    }

    /**
     * Answer'ı işle (mobil'den)
     */
    private async handleAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
        if (!this.peerConnection) return;

        try {
            await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
        } catch (error) {
            console.error('[RemoteCamera] Answer işleme hatası:', error);
        }
    }

    /**
     * ICE candidate'ı işle
     */
    private async handleIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
        if (!this.peerConnection) return;

        try {
            await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (error) {
            console.error('[RemoteCamera] ICE candidate hatası:', error);
        }
    }

    /**
     * Frame yakalama başlat
     */
    private startFrameCapture(stream: MediaStream): void {
        console.log('[RemoteCamera] Frame capture başlatılıyor...');

        this.videoElement = document.createElement('video');
        this.videoElement.srcObject = stream;
        this.videoElement.autoplay = true;
        this.videoElement.playsInline = true;
        this.videoElement.muted = true;

        this.videoElement.onloadedmetadata = () => {
            if (!this.videoElement || !this.captureCanvas || !this.captureContext) return;

            this.captureCanvas.width = this.videoElement.videoWidth;
            this.captureCanvas.height = this.videoElement.videoHeight;

            const currentInfo = this.cameraInfo$.value;
            if (currentInfo) {
                currentInfo.resolution = {
                    width: this.videoElement.videoWidth,
                    height: this.videoElement.videoHeight
                };
                this.cameraInfo$.next(currentInfo);
            }

            console.log('[RemoteCamera] Video metadata yüklendi:', this.videoElement.videoWidth, 'x', this.videoElement.videoHeight);
        };

        this.videoElement.oncanplay = () => {
            console.log('[RemoteCamera] Video oynatılabilir durumda');
        };

        this.videoElement.onerror = (e) => {
            console.error('[RemoteCamera] Video element hatası:', e);
        };

        // Explicit play
        this.videoElement.play().then(() => {
            console.log('[RemoteCamera] Internal video oynatma başladı');
        }).catch(err => {
            console.error('[RemoteCamera] Internal video oynatma hatası:', err);
        });
    }

    /**
     * Mevcut frame'i yakala
     */
    public captureFrame(): ImageData | null {
        if (!this.videoElement || !this.captureCanvas || !this.captureContext) {
            return null;
        }

        this.captureContext.drawImage(
            this.videoElement,
            0, 0,
            this.captureCanvas.width,
            this.captureCanvas.height
        );

        const imageData = this.captureContext.getImageData(
            0, 0,
            this.captureCanvas.width,
            this.captureCanvas.height
        );

        this.frameCapture$.next(imageData);
        return imageData;
    }

    /**
     * Otomatik frame yakalama başlat
     */
    public startAutoCapture(fps: number = 30): void {
        this.stopAutoCapture();

        const interval = 1000 / fps;
        this.captureInterval = setInterval(() => {
            this.captureFrame();
        }, interval);
    }

    /**
     * Otomatik frame yakalama durdur
     */
    public stopAutoCapture(): void {
        if (this.captureInterval) {
            clearInterval(this.captureInterval);
            this.captureInterval = null;
        }
    }

    /**
     * Kontrol komutu gönder
     */
    public sendControl(command: string, params: any = {}): void {
        this.socket?.emit('FikoYT.cmm.camera.control', {
            sessionId: this.sessionId,
            command: command,
            params: params
        });
    }

    /**
     * Flaş aç/kapat
     */
    public setTorch(enabled: boolean): void {
        this.sendControl('torch', { enabled });
    }

    /**
     * Odaklama noktası ayarla
     */
    public setFocusPoint(x: number, y: number): void {
        this.sendControl('focus', { point: { x, y } });
    }

    /**
     * Pozlama ayarla
     */
    public setExposure(value: number): void {
        this.sendControl('exposure', { value });
    }

    /**
     * Bağlantı kesildi
     */
    private handleDisconnect(): void {
        this.state$.next('disconnected');
        this.videoStream$.next(null);
        this.cameraInfo$.next(null);
        this.stopAutoCapture();
    }

    /**
     * Bağlantıyı kapat
     */
    public disconnect(): void {
        this.stopAutoCapture();

        // Eşleştirme iptal
        if (this.socket && this.sessionId) {
            this.socket.emit('FikoYT.cmm.pairing.cancel', {
                sessionId: this.sessionId
            });
        }

        if (this.dataChannel) {
            this.dataChannel.close();
            this.dataChannel = null;
        }

        if (this.peerConnection) {
            this.peerConnection.close();
            this.peerConnection = null;
        }

        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }

        this.videoStream$.next(null);
        this.cameraInfo$.next(null);
        this.pairing$.next(null);
        this.state$.next('disconnected');
    }

    /**
     * Session ID oluştur
     */
    private generateSessionId(): string {
        return 'cmm_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
    }

    /**
     * Eşleştirme kodu oluştur (6 haneli)
     */
    private generatePairingCode(): string {
        return Math.floor(100000 + Math.random() * 900000).toString();
    }

    /**
     * QR kod oluştur
     */
    private async generateQRCode(url: string): Promise<string> {
        try {
            const qrDataUrl = await QRCode.toDataURL(url, {
                width: 200,
                margin: 2,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                },
                errorCorrectionLevel: 'M'
            });
            return qrDataUrl;
        } catch (error) {
            console.error('[RemoteCamera] QR kod oluşturma hatası:', error);
            return this.createPlaceholderQR();
        }
    }

    /**
     * Fallback placeholder QR
     */
    private createPlaceholderQR(): string {
        const canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 200;
        const ctx = canvas.getContext('2d')!;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 200, 200);
        ctx.fillStyle = '#cccccc';
        ctx.font = '14px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('QR Kod', 100, 100);

        return canvas.toDataURL('image/png');
    }

    /**
     * Son IMU verisini al
     */
    public getLastIMUData(): IMUSensorData | null {
        return this.lastIMUData;
    }

    /**
     * Temizle
     */
    public dispose(): void {
        this.disconnect();

        this.state$.complete();
        this.cameraInfo$.complete();
        this.videoStream$.complete();
        this.frameCapture$.complete();
        this.error$.complete();
        this.pairing$.complete();
        this.scanControl$.complete();
        this.imuData$.complete();
        this.lastIMUData = null;
    }
}
